/**
 * КОНТРАКТ ТЕМЫ — единственный источник правды о том, из чего состоит тема.
 *
 * Тема — это ровно этот список переменных и ничего больше. Ни отступа,
 * ни шрифта, ни длительности: геометрия и время живут в scales.css и от темы
 * не зависят.
 *
 * Зачем список отдельным файлом, а не «просто CSS»:
 * тест `themes.test.ts` читает все файлы тем и проверяет, что каждая объявляет
 * КАЖДУЮ роль. Без этого пятая тема однажды забудет одну переменную, унаследует
 * её от обсидиана — и что-то сломается только в ней и только на одном экране.
 * Такие вещи ищут вечер.
 *
 * Добавить роль: дописать сюда → тест покраснеет на всех темах → заполнить.
 * Добавить тему: скопировать obsidian.css → тест скажет, чего не хватает.
 */

export const THEME_ROLES = [
  // ── Поверхности: шесть уровней вместо двух ──────────────────────────
  'bg-canvas', //         фон приложения
  'bg-canvas-deep', //    за скроллом, оверскролл
  'surface-sunken', //    инпуты, строки списка
  'surface', //           карточки, тайлы
  'surface-raised', //    шторки, модалки
  'surface-glass', //     таббар, sticky-футер (полупрозрачное)

  // ── Границы: три силы вместо одной ──────────────────────────────────
  'border-subtle',
  'border',
  'border-strong',
  'border-sacred', //     рамки карт

  // ── Текст: четыре роли вместо пяти неотличимых серых ────────────────
  'text-primary',
  'text-secondary', //    тело предсказания
  'text-tertiary', //     подписи, мета
  'text-on-accent', //    на акцентной кнопке
  'text-on-sacred', //    на золотой плашке

  // ── Интерактив ──────────────────────────────────────────────────────
  'accent',
  'accent-hover',
  'accent-bg',

  // ── Сакральное. Никогда не кликается ────────────────────────────────
  'sacred',
  'sacred-bright',
  'sacred-dim',

  // ── Статусы ─────────────────────────────────────────────────────────
  'success',
  'success-bg',
  'danger',
  'danger-bg',
  'warning',
  'warning-bg',
  'arcane', //            перевёрнутая карта, паттерны, доказательство честности
  'arcane-bg',

  // ── Материал: из чего сделана поверхность ───────────────────────────
  // Слой, которого в контракте не было и без которого тема — это раскраска.
  // Тема отвечает не только за цвет, но и за то, КАК на поверхность падает
  // свет: где блик, где фаска, какое зерно. Обсидиан отражает, пергамент
  // впитывает — разница выражается здесь, а не в компонентах.
  'mat-specular', //      блик: узкая полоса там, куда бьёт свет
  'mat-bevel-hi', //      верхняя грань фаски (к свету)
  'mat-bevel-lo', //      нижняя грань фаски (от света)
  'mat-holo', //          градиент фольги. Только карта дня, только редко
  'mat-grain-opacity', // сила зерна: 0.05 на обсидиане, 0.09 на бумаге
  'mat-grain-blend', //   soft-light на тёмном, multiply на бумаге

  // ── Атмосфера: воздух между зрителем и картой ───────────────────────
  // Три пятна тумана дрейфуют за контентом по 60–90 секунд. В пергаменте
  // тумана нет — там воздуха не бывает, там бумага. Роли всё равно
  // объявлены: тема обязана СКАЗАТЬ, что тумана нет, а не промолчать.
  'atm-fog-a',
  'atm-fog-b',
  'atm-fog-c',
  'atm-vignette', //      затемнение к краям экрана
  'atm-dust', //          пылинки в луче и звёзды

  // ── Служебное ───────────────────────────────────────────────────────
  'scrim', //             затемнение под модалкой
  'focus-ring',
  'rim-light', //         «свет сверху» на поверхностях. В светлой теме инвертируется
  'card-mat', //          подложка под карту. В тёмных темах none: чёрная рубашка
  //                      на светлом фоне без неё висит дырой
  'shadow-color', //      цвет теней: у бумаги он не чёрный, а сепия

  // ── Составные ───────────────────────────────────────────────────────
  'grad-cta',
  'grad-hairline', //     золотая нить: верх шторки, разделители
  'tex-opacity', //       текстура зодиакального колеса
] as const

export type ThemeRole = (typeof THEME_ROLES)[number]

/** Имена тем. Обсидиан — база и значение по умолчанию. */
export const THEMES = ['obsidian', 'sapphire', 'parchment', 'crimson'] as const

export type ThemeName = (typeof THEMES)[number]

export const DEFAULT_THEME: ThemeName = 'obsidian'

/** Светлая тема нужна отдельно: под неё меняется цвет нативного хрома. */
export const LIGHT_THEMES: readonly ThemeName[] = ['parchment']

export interface ThemeMeta {
  name: ThemeName
  /** Как называется для пользователя. */
  title: string
  /** Одной фразой — настроение, а не перечисление цветов. */
  mood: string
  scheme: 'dark' | 'light'
}

export const THEME_META: Record<ThemeName, ThemeMeta> = {
  obsidian: {
    name: 'obsidian',
    title: 'Обсидиан',
    mood: 'Ночь, чёрное дерево, тиснёное золото',
    scheme: 'dark',
  },
  sapphire: {
    name: 'sapphire',
    title: 'Полночная лазурь',
    mood: 'Луна, Звезда, вода и Кубки',
    scheme: 'dark',
  },
  parchment: {
    name: 'parchment',
    title: 'Пергамент',
    mood: 'Бумага, чернила, терракота',
    scheme: 'light',
  },
  crimson: {
    name: 'crimson',
    title: 'Багряный обет',
    mood: 'Вино, воск, бархат. Дьявол и Башня',
    scheme: 'dark',
  },
}
